import {
  CheckCircle2,
  Grid,
  Package,
  Search,
  TrendingDown,
  TrendingUp,
  X,
  Lock,
  Upload,
  Download,
  Plus,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { useWirelessPrinter } from '@/features/hardware/hooks/useWirelessPrinter';
import { compileWirelessLabelsBulk } from '@/features/hardware/utils/compileWirelessLabels';
import { Bluetooth } from 'lucide-react';
import { BulkMatrixIntake } from '../components/BulkMatrixIntake';

interface ProductItem {
  id: string;
  productId?: string;
  isVariant?: boolean;
  sku: string;
  name: string;
  category: string;
  color: string;
  size: string;
  currentStock: number;
  costPrice: number;
  sellingPrice: number;
  mrp?: number;
  discount_percentage?: number;
  soldStock?: number;
  initialStock?: number;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
}

export function InventoryDashboard() {
  const { user } = useAuth();
  const tenantFlags = JSON.parse(
    localStorage.getItem(`tenant_flags_${user?.tenantId}`) ||
    '{"bulkMatrix":false,"gstComplianceExport":false,"loyaltyEngine":false}',
  );

  const [isGstEnabled, setIsGstEnabled] = useState(false);
  const [printConfig, setPrintConfig] = useState<any>(null);

  useEffect(() => {
    if (!user?.tenantId) return;
    api
      .get('/tenant/settings/company-profile', {
        headers: { 'x-tenant-id': user.tenantId },
      })
      .then((res) => {
        const isEligible = res.data.data?.is_gst_eligible ?? false;
        setIsGstEnabled(!!(tenantFlags.gstComplianceExport && isEligible));
        setPrintConfig(res.data.data?.print_config || null);
      })
      .catch(() => {
        setIsGstEnabled(false);
      });
  }, [user?.tenantId, tenantFlags.gstComplianceExport]);

  const {
    isConnected: isPrinterConnected,
    isPrinting: isPrinterPrinting,
    pairedDevice,
    error: printerError,
    connectPrinter,
    disconnectPrinter,
    sendPrintJob,
  } = useWirelessPrinter();

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [viewMode, setViewMode] = useState<'LIST' | 'MATRIX' | 'BULK_UPLOAD' | 'LABEL_PRINTER'>(
    'LIST',
  );
  const [searchTerm, setSearchTerm] = useState('');

  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [editCostPrice, setEditCostPrice] = useState<number>(0);
  const [editSellingPrice, setEditSellingPrice] = useState<number>(0);
  const [editMrp, setEditMrp] = useState<number>(0);
  const [editDiscountPercentage, setEditDiscountPercentage] = useState<number>(0);
  const [editStock, setEditStock] = useState<number>(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const startEdit = (item: any) => {
    setEditingItem(item);
    setEditCostPrice(item.costPrice);
    setEditSellingPrice(item.sellingPrice);
    setEditMrp(item.mrp || item.sellingPrice);
    setEditDiscountPercentage(item.discount_percentage || 0);
    setEditStock(item.currentStock);
    setUpdateError('');
  };

  const handleEditFieldChange = (field: 'MRP' | 'DISCOUNT' | 'SELL' | 'COST' | 'STOCK', val: number) => {
    if (field === 'COST') setEditCostPrice(val);
    else if (field === 'STOCK') setEditStock(val);
    else if (field === 'MRP') {
      setEditMrp(val);
      if (editDiscountPercentage > 0) {
        setEditSellingPrice(Math.round(val * (1 - editDiscountPercentage / 100) * 100) / 100);
      } else {
        setEditSellingPrice(val);
      }
    } else if (field === 'DISCOUNT') {
      setEditDiscountPercentage(val);
      if (editMrp > 0) {
        setEditSellingPrice(Math.round(editMrp * (1 - val / 100) * 100) / 100);
      }
    } else if (field === 'SELL') {
      setEditSellingPrice(val);
      if (editMrp > 0) {
        const disc = ((editMrp - val) / editMrp) * 100;
        setEditDiscountPercentage(Math.round(disc * 100) / 100);
      }
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    setIsUpdating(true);
    setUpdateError('');

    try {
      const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
      const endpoint = editingItem.productId && editingItem.isVariant
        ? `/inventory/variants/${editingItem.id}`
        : `/inventory/products/${editingItem.id}`;

      const payload = editingItem.productId && editingItem.isVariant ? {
        costPrice: editCostPrice,
        retail_price: editSellingPrice,
        mrp: editMrp,
        discount_percentage: editDiscountPercentage,
        currentStock: editStock
      } : {
        costPrice: editCostPrice,
        sellingPrice: editSellingPrice,
        mrp: editMrp,
        currentStock: editStock
      };

      const res = await api.patch(endpoint, payload, { headers });
      if (res.data.success) {
        setEditingItem(null);
        await fetchProducts();
      }
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || 'Failed to update catalog item.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Label printer state variables
  const [selectedSkuForLabel, setSelectedSkuForLabel] = useState('');
  const [labelFormat, setLabelFormat] = useState<'BARCODE' | 'QR_CODE'>('BARCODE');
  const [labelSize, setLabelSize] = useState<'2x1' | '3x2' | '4x3'>('2x1');
  const [labelCopies, setLabelCopies] = useState<number>(1);

  // Categories Manager states
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [categorySuccess, setCategorySuccess] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Bulk Upload states
  const [parsedProducts, setParsedProducts] = useState<
    Array<{
      name: string;
      sku: string;
      hsnCode: string;
      categoryName: string;
      costPrice: number;
      mrp: number;
      discount_percentage: number;
      sellingPrice: number;
      currentStock: number;
      lowStockThreshold: number;
      unit: string;
      color: string;
      size: string;
    }>
  >([]);
  const [bulkIngestError, setBulkIngestError] = useState('');
  const [bulkIngestSuccess, setBulkIngestSuccess] = useState('');
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
      const response = await api.get('/inventory/categories', { headers });
      setCategories(response.data.data || []);
    } catch (err) {
      console.error('Failed to retrieve categories.');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
      const response = await api.get('/inventory/products', { headers });
      const backendProducts: ProductItem[] = [];
      response.data.data.forEach((p: any) => {
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach((v: any) => {
            const soldVal = v.invoiceItems?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
            backendProducts.push({
              id: v.id,
              productId: p.id,
              isVariant: true,
              sku: v.sku,
              name: `${p.name} (${v.color || 'N/A'} / ${v.size || 'N/A'})`,
              category: p.category?.name || 'Uncategorized',
              color: v.color || 'N/A',
              size: v.size || 'N/A',
              currentStock: v.currentStock || 0,
              costPrice: v.costPrice || 0,
              sellingPrice: v.retail_price || 0,
              mrp: v.mrp || 0,
              discount_percentage: v.discount_percentage || 0,
              soldStock: soldVal,
              initialStock: (v.currentStock || 0) + soldVal,
              status: v.currentStock === 0 ? 'OUT_OF_STOCK' : v.currentStock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'
            });
          });
        } else {
          const soldVal = p.invoiceItems?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
          backendProducts.push({
            id: p.id,
            isVariant: false,
            sku: p.sku,
            name: p.name,
            category: p.category?.name || 'Uncategorized',
            color: p.color || 'N/A',
            size: p.size || 'N/A',
            currentStock: p.currentStock || 0,
            costPrice: p.costPrice || 0,
            sellingPrice: p.sellingPrice || 0,
            mrp: p.mrp || p.sellingPrice || 0,
            discount_percentage: 0,
            soldStock: soldVal,
            initialStock: (p.currentStock || 0) + soldVal,
            status: p.currentStock === 0 ? 'OUT_OF_STOCK' : p.currentStock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'
          });
        }
      });
      setProducts(backendProducts);
    } catch (err) {
      console.error('Failed to load products.');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleAddCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setCategoryError('');
    setCategorySuccess('');

    try {
      const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
      await api.post('/inventory/categories', { name: newCategoryName.trim() }, { headers });
      setCategorySuccess(`Category "${newCategoryName}" created successfully!`);
      setNewCategoryName('');
      await fetchCategories();
      setTimeout(() => setCategorySuccess(''), 3000);
    } catch (err: any) {
      setCategoryError(err.response?.data?.message || 'Failed to create category.');
    }
  };

  const handleDeleteProduct = async (item: ProductItem) => {
    const productIdToDelete = item.isVariant ? item.productId : item.id;
    if (!productIdToDelete) return;

    if (
      window.confirm(
        `Are you sure you want to delete "${item.name}" and all its associated variants/stock?`
      )
    ) {
      try {
        const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
        const res = await api.delete(`/inventory/products/${productIdToDelete}`, { headers });
        if (res.data.success) {
          await fetchProducts();
        }
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to delete product.');
      }
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the category "${name}"? Products inside this category will be uncategorized.`
      )
    ) {
      try {
        const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
        const res = await api.delete(`/inventory/categories/${id}`, { headers });
        if (res.data.success) {
          setCategorySuccess(`Category "${name}" deleted successfully.`);
          await fetchCategories();
          await fetchProducts();
          setTimeout(() => setCategorySuccess(''), 3000);
        }
      } catch (err: any) {
        setCategoryError(err.response?.data?.message || 'Failed to delete category.');
      }
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);


  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.category.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-surface-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 tracking-tight">
            Inventory Management
          </h1>
          <p className="mt-1 text-sm text-surface-500 font-light">
            Audit catalog products, configure store categories, or bulk ingest variant spreadsheets.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Categories Manager toggle */}
          <button
            onClick={() => setShowCategoryManager(!showCategoryManager)}
            className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
            title="Manage Dress Categories"
          >
            <Plus size={14} className="text-slate-500" />
            <span>Dress Categories</span>
          </button>

          {/* Segmented Controls for Mode views */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            <button
              onClick={() => setViewMode('LIST')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === 'LIST'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Catalog List
            </button>
            <button
              onClick={() => setViewMode('MATRIX')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === 'MATRIX'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Matrix Intake
            </button>
            <button
              onClick={() => setViewMode('BULK_UPLOAD')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === 'BULK_UPLOAD'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Spreadsheet Ingest
            </button>
            <button
              onClick={() => setViewMode('LABEL_PRINTER')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === 'LABEL_PRINTER'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              Label Printer
            </button>
          </div>
        </div>
      </div>

      {/* Category Manager Drawer/Card */}
      {showCategoryManager && (
        <div className="card bg-slate-50 border border-slate-200 p-5 rounded-xl shadow-inner relative space-y-4">
          <button
            type="button"
            onClick={() => setShowCategoryManager(false)}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>

          <div>
            <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Plus size={14} className="text-emerald-600" />
              <span>Create Store Dress Categories</span>
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Add custom categories like Pant, Shirt, Innerwear, Saree for your clothing store
              catalog.
            </p>
          </div>

          <form onSubmit={handleAddCategorySubmit} className="flex gap-2 max-w-md">
            <input
              type="text"
              required
              placeholder="e.g. Shirt, Pant, Innerwear..."
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="flex-1 px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-emerald-600 font-medium"
            />
            <button
              type="submit"
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-sm border border-emerald-500"
            >
              Add Category
            </button>
          </form>

          {categoryError && (
            <div className="text-[11px] text-rose-650 font-bold">{categoryError}</div>
          )}
          {categorySuccess && (
            <div className="text-[11px] text-emerald-655 font-bold">{categorySuccess}</div>
          )}

          {/* Current list of Categories */}
          <div className="space-y-1.5 pt-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase block">
              Active Categories Registry:
            </span>
            {loadingCategories ? (
              <span className="text-[10px] text-slate-400">Loading categories...</span>
            ) : categories.length === 0 ? (
              <span className="text-[10px] text-slate-400">
                No custom categories added yet. Add "Pant" or "Shirt" above!
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => (
                  <span
                    key={c.id}
                    className="pl-2.5 pr-1.5 py-0.5 rounded bg-white text-slate-650 text-[10px] font-bold border border-slate-150 shadow-sm flex items-center gap-1.5 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors group"
                  >
                    <span>{c.name}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(c.id, c.name)}
                      className="text-slate-400 hover:text-rose-600 transition-colors focus:outline-none cursor-pointer"
                      title={`Delete category ${c.name}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'LIST' ? (
        /* ─── CATALOG LIST VIEW ────────────────────────────────────── */
        <div className="space-y-6">
          {/* Inventory Stats cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <Package size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  SKUs Registered
                </p>
                <p className="text-xl font-bold text-slate-800">{products.length}</p>
              </div>
            </div>
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <TrendingDown size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Low/Out of Stock
                </p>
                <p className="text-xl font-bold text-slate-800">
                  {products.filter((p) => p.status !== 'IN_STOCK').length}
                </p>
              </div>
            </div>
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <TrendingUp size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Stock Valuation (Cost)
                </p>
                <p className="text-xl font-bold text-slate-800">
                  ₹
                  {products
                    .reduce((sum, p) => sum + p.currentStock * p.costPrice, 0)
                    .toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Catalog grid */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                Active Inventory Catalog
              </h2>
              <div className="relative w-64">
                <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by keyword, SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                    <th className="py-2.5 px-3 text-center">S.No.</th>
                    <th className="py-2.5 px-3">SKU</th>
                    <th className="py-2.5 px-2">Product Name</th>
                    <th className="py-2.5 px-2">Category</th>
                    <th className="py-2.5 px-2">Attr (Color/Size)</th>
                    <th className="py-2.5 px-2 text-right">Cost (₹)</th>
                    <th className="py-2.5 px-2 text-right">Price (₹)</th>
                    <th className="py-2.5 px-2 text-center">Stock (Rem/Sold/Tot)</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                    <th className="py-2.5 px-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingProducts ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-slate-400 font-semibold">
                        <span className="inline-block animate-pulse">Fetching inventory catalog products...</span>
                      </td>
                    </tr>
                  ) : filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-slate-400 font-semibold">
                        No products match your search criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((p, idx) => (
                      <tr key={p.sku} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-707">{p.sku}</td>
                        <td className="py-2.5 px-2 font-bold text-slate-800">{p.name}</td>
                        <td className="py-2.5 px-2 text-slate-500">{p.category}</td>
                        <td className="py-2.5 px-2">
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium mr-1">{p.color}</span>
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-606 rounded text-[10px] font-medium">{p.size}</span>
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono font-medium text-slate-600">₹{p.costPrice.toFixed(2)}</td>
                        <td className="py-2.5 px-2 text-right font-mono">
                          {p.mrp && p.mrp > p.sellingPrice ? (
                            <div className="flex flex-col items-end">
                              <span className="text-[10px] text-slate-400 line-through">₹{p.mrp.toFixed(2)}</span>
                              <span className="font-bold text-emerald-800">₹{p.sellingPrice.toFixed(2)}</span>
                              {p.discount_percentage || (p.mrp && p.mrp > p.sellingPrice) ? (
                                <span className="text-[8px] bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded font-extrabold mt-0.5">-{Math.round(p.discount_percentage || (((p.mrp - p.sellingPrice) / p.mrp) * 100))}%</span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="font-semibold text-slate-800">₹{p.sellingPrice.toFixed(2)}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-center font-mono">
                          <div className="flex flex-col items-center">
                            <span className="font-bold text-slate-800" title="Remaining Stock">Rem: {p.currentStock}</span>
                            <span className="text-[10px] text-slate-400 mt-0.5" title="Sold Stock">Sold: {p.soldStock || 0}</span>
                            <span className="text-[9px] text-emerald-605 font-semibold mt-0.5" title="Total Ingested Stock">Tot: {p.initialStock || p.currentStock}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span
                            className={`text-[9px] px-1.5 py-0.5 font-bold rounded ${p.status === 'IN_STOCK'
                              ? 'bg-emerald-100 text-emerald-800'
                              : p.status === 'LOW_STOCK'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {p.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(p)}
                            className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-700 rounded border border-slate-200 text-[10px] font-bold transition-all cursor-pointer shadow-sm active:scale-95"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteProduct(p)}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded border border-rose-200 text-[10px] font-bold transition-all cursor-pointer shadow-sm active:scale-95"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : viewMode === 'MATRIX' ? (
        !tenantFlags.bulkMatrix && user?.role !== 'SUPER_ADMIN' ? (
          /* ─── BULK MATRIX PREMIUM LOCKED OVERLAY ─────────────────── */
          <div className="flex flex-col items-center justify-center max-w-xl mx-auto text-center space-y-6 py-12">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500 shadow-md">
              <Lock size={32} className="animate-bounce" />
            </div>
            <div className="space-y-2">
              <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider bg-amber-50 border border-amber-200/50 px-2.5 py-1 rounded-full">
                Premium Module Locked
              </span>
              <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
                Unlock Multi-Dimensional Variant Grids
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                Your current subscription (Silver) does not include the bulk multi-variant intake
                grid. Upgrade to Gold or Platinum to bulk ingest matrices of colors, sizes, and
                stock limits.
              </p>
            </div>
            <div className="w-full bg-white border border-slate-150 p-5 rounded-2xl text-left text-xs space-y-3 shadow-sm">
              <span className="font-bold text-slate-700 block border-b border-slate-105 pb-2">
                Unlocked on Gold & Platinum:
              </span>
              <ul className="space-y-2.5 text-slate-500 font-semibold">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>
                    Input unlimited variations of apparel sizes and customized fabric colors.
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>
                    Simultaneously inject cost prices, selling prices, and base stock levels.
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>
                    Instantly auto-generate clean SKUs and barcodes for retail cashiering.
                  </span>
                </li>
              </ul>
            </div>
            <p className="text-xs text-slate-400 font-medium">
              Please contact the platform system administrator at{' '}
              <span className="font-bold text-emerald-600">support@threads.com</span> to upgrade
              your license to Gold or Platinum.
            </p>
          </div>
        ) : (
          <BulkMatrixIntake
            user={user}
            categories={categories}
            onSuccess={() => {
              fetchProducts();
              setViewMode('LIST');
            }}
            onCancel={() => setViewMode('LIST')}
          />
        )
      ) : viewMode === 'BULK_UPLOAD' ? (
        /* ─── SPREADSHEET BULK UPLOAD VIEW ─────────────────────────── */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* File selector Dropzone (Left 1/3) */}
          <div className="space-y-6">
            <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm space-y-4">
              <div>
                <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Upload size={14} className="text-emerald-600" />
                  <span>Spreadsheet Upload Ingest</span>
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Select a filled CSV inventory template to bulk upload fabric and dress catalogs.
                </p>
              </div>

              {/* Sample Template download button */}
              <button
                type="button"
                onClick={() => {
                  const headers = isGstEnabled
                    ? 'Product Name *,SKU,HSN Code,Category *,Cost Price *,MRP *,Discount %,Current Stock,Low Stock Threshold,Unit,Color,Size'
                    : 'Product Name *,SKU,Category *,Cost Price *,MRP *,Discount %,Current Stock,Low Stock Threshold,Unit,Color,Size';

                  const sampleRows = isGstEnabled
                    ? [
                      'Silk Saree Blend,SILK-SAR-OS,5007,Ethnic Wear,1200,1999,10,50,5,PIECES,Red,FreeSize',
                      'Linen Men Shirt,,6205,Shirt,450,999,0,100,10,PIECES,Blue,L',
                      'Cotton Fit Jeans,CTN-JNS-32,6203,Pant,600,1399,15,80,10,PIECES,Black,32',
                    ]
                    : [
                      'Silk Saree Blend,SILK-SAR-OS,Ethnic Wear,1200,1999,10,50,5,PIECES,Red,FreeSize',
                      'Linen Men Shirt,,Shirt,450,999,0,100,10,PIECES,Blue,L',
                      'Cotton Fit Jeans,CTN-JNS-32,Pant,600,1399,15,80,10,PIECES,Black,32',
                    ];

                  // Add custom tenant categories dynamically!
                  if (categories.length > 0) {
                    categories.slice(0, 3).forEach((c) => {
                      if (isGstEnabled) {
                        sampleRows.push(
                          `Dynamic Sample ${c.name},,5007,${c.name},250,599,0,100,10,METERS,Green,M`,
                        );
                      } else {
                        sampleRows.push(
                          `Dynamic Sample ${c.name},,,${c.name},250,599,0,100,10,METERS,Green,M`,
                        );
                      }
                    });
                  }

                  const csvContent =
                    'data:text/csv;charset=utf-8,' + [headers, ...sampleRows].join('\n');
                  const encodedUri = encodeURI(csvContent);
                  const link = document.createElement('a');
                  link.setAttribute('href', encodedUri);
                  link.setAttribute('download', `textile_pos_inventory_template.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="w-full py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-emerald-100 cursor-pointer shadow-sm"
              >
                <Download size={14} />
                <span>Download Sample Template</span>
              </button>

              {/* Dynamic File Selector Dropzone */}
              <div className="border-2 border-dashed border-slate-200 hover:border-emerald-500 rounded-xl p-6 text-center bg-slate-50 transition-all cursor-pointer relative group">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const text = event.target?.result as string;
                      if (!text) return;

                      const lines = text
                        .split('\n')
                        .map((l) => l.trim())
                        .filter((l) => l.length > 0);
                      if (lines.length <= 1) {
                        setBulkIngestError('The spreadsheet file is empty or missing data rows.');
                        return;
                      }

                      // Dynamic Column Index Parser based on CSV headers
                      const headerRow = lines[0];
                      if (!headerRow) return;
                      const headerCells = headerRow.split(',').map((c) =>
                        c
                          .trim()
                          .replace(/^["']|["']$/g, '')
                          .replace(/\s*\*/g, '')
                          .toLowerCase(),
                      );
                      const nameIdx = headerCells.indexOf('product name');
                      const skuIdx = headerCells.indexOf('sku');
                      const hsnIdx = headerCells.indexOf('hsn code');
                      const categoryIdx = headerCells.indexOf('category');
                      const costIdx = headerCells.indexOf('cost price');
                      const mrpIdx = headerCells.indexOf('mrp');
                      const discountIdx = headerCells.indexOf('discount %');
                      const sellIdx = headerCells.indexOf('selling price');
                      const stockIdx = headerCells.indexOf('current stock');
                      const thresholdIdx = headerCells.indexOf('low stock threshold');
                      const unitIdx = headerCells.indexOf('unit');
                      const colorIdx = headerCells.indexOf('color');
                      const sizeIdx = headerCells.indexOf('size');

                      const errors: string[] = [];

                      if (nameIdx === -1) {
                        errors.push("Mandatory column 'Product Name *' is missing in CSV headers.");
                      }
                      if (categoryIdx === -1) {
                        errors.push("Mandatory column 'Category *' is missing in CSV headers.");
                      }
                      if (costIdx === -1) {
                        errors.push("Mandatory column 'Cost Price *' is missing in CSV headers.");
                      }
                      if (mrpIdx === -1) {
                        errors.push("Mandatory column 'MRP *' is missing in CSV headers.");
                      }

                      if (errors.length > 0) {
                        setBulkIngestError("Header Ingestion Error:\n" + errors.join('\n'));
                        setParsedProducts([]);
                        return;
                      }

                      const parsed = [];
                      for (let i = 1; i < lines.length; i++) {
                        const rawLine = lines[i];
                        if (!rawLine) continue;
                        const line = rawLine.trim();
                        if (!line) continue;
                        const cells = line
                          .split(',')
                          .map((c) => c.trim().replace(/^["']|["']$/g, ''));
                        
                        // Check if line contains any values (skip fully empty rows)
                        const hasValues = cells.some((c) => c.length > 0);
                        if (!hasValues) continue;

                        const name = (nameIdx !== -1 ? cells[nameIdx] : '') || '';
                        const sku = (skuIdx !== -1 ? cells[skuIdx] : '') || '';
                        const hsnCode = (hsnIdx !== -1 ? cells[hsnIdx] : '') || '';
                        const categoryName = (categoryIdx !== -1 ? cells[categoryIdx] : '') || '';

                        const rowErrors: string[] = [];
                        if (!name.trim()) {
                          rowErrors.push("'Product Name' is empty");
                        }
                        if (!categoryName.trim()) {
                          rowErrors.push("'Category' is empty");
                        }

                        const rawCost = (costIdx !== -1 ? cells[costIdx] : '') || '';
                        if (rawCost === '' || isNaN(Number(rawCost))) {
                          rowErrors.push("'Cost Price' is missing or not a valid number");
                        }
                        const costPrice = Number(rawCost) || 0;

                        const rawMrp = (mrpIdx !== -1 ? cells[mrpIdx] : '') || '';
                        if (rawMrp === '' || isNaN(Number(rawMrp))) {
                          rowErrors.push("'MRP' is missing or not a valid number");
                        }
                        const mrp = Number(rawMrp) || 0;

                        if (rowErrors.length > 0) {
                          errors.push(`Row ${i + 1}: ${rowErrors.join(', ')}.`);
                          continue;
                        }

                        const discount = discountIdx !== -1 ? (Number(cells[discountIdx]) || 0) : 0;
                        const sellingPrice = sellIdx !== -1 && mrpIdx === -1 ? (Number(cells[sellIdx]) || 0) : mrp * (1 - discount / 100);
                        const currentStock = stockIdx !== -1 ? (Number(cells[stockIdx]) || 0) : 0;
                        const lowStockThreshold =
                          thresholdIdx !== -1 ? (Number(cells[thresholdIdx]) || 10) : 10;
                        const unit = (unitIdx !== -1 ? cells[unitIdx] : 'METERS') || 'METERS';
                        const color = (colorIdx !== -1 ? cells[colorIdx] : '') || '';
                        const size = (sizeIdx !== -1 ? cells[sizeIdx] : '') || '';

                        parsed.push({
                          name: name.trim(),
                          sku: sku.trim() || (() => {
                            const slugName = Math.floor(100 + Math.random() * 900).toString();
                            const slugColor = (color || 'XX').trim().substring(0, 2).toUpperCase();
                            const slugSize = (size || 'XX').trim().toUpperCase();
                            return `${slugName}-${slugColor}-${slugSize}`;
                          })(),
                          hsnCode: hsnCode || '',
                          categoryName: categoryName.trim(),
                          costPrice,
                          mrp,
                          discount_percentage: discount,
                          sellingPrice: Number(sellingPrice) || 0,
                          currentStock: Number(currentStock) || 0,
                          lowStockThreshold: Number(lowStockThreshold) || 10,
                          unit: unit || 'METERS',
                          color: color || '',
                          size: size || '',
                        });
                      }

                      if (errors.length > 0) {
                        setBulkIngestError(
                          "Spreadsheet validation failed. Please correct and reupload the file:\n" +
                          errors.slice(0, 15).join('\n') +
                          (errors.length > 15 ? `\n...and ${errors.length - 15} more rows with errors.` : '')
                        );
                        setParsedProducts([]);
                        return;
                      }

                      setParsedProducts(parsed);
                      setBulkIngestError('');
                    };
                    reader.readAsText(file);
                    e.target.value = '';
                  }}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
                <FileSpreadsheet
                  size={36}
                  className="mx-auto mb-2 text-slate-400 group-hover:text-emerald-500 transition-colors"
                />
                <span className="text-[11px] text-slate-500 font-bold block">
                  Select filled CSV Template
                </span>
                <span className="text-[9px] text-slate-400 mt-1 block">
                  Supports standard `.csv` values
                </span>
              </div>
            </div>

            {/* Instruction Card */}
            <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm text-xs space-y-3.5 font-medium text-slate-500 leading-relaxed">
              <span className="font-bold text-slate-800 block border-b border-slate-100 pb-2 uppercase tracking-wide text-[10px]">
                Ingestion Steps
              </span>
              <ul className="space-y-3 text-[11px]">
                <li>
                  1. Download the custom template above (it dynamically updates columns according to
                  your store setup).
                </li>
                <li>2. Open in Excel, Numbers, or Sheets and add products.</li>
                <li>
                  3. Fill out all{' '}
                  <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded text-[10px]">
                    Mandatory Green Fields *
                  </span>
                  :
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-semibold">
                      Product Name *
                    </span>
                    <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-semibold">
                      Category *
                    </span>
                    <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-semibold">
                      Cost Price *
                    </span>
                    <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-semibold">
                      MRP *
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Note: SKU is optional and will be automatically generated if left blank. Selling Price is calculated automatically using MRP and Discount %.
                  </span>
                </li>
                <li>4. Utilize custom categories (Pant, Shirt) for accurate grouping.</li>
                <li>
                  5. Optional attributes: Fill the{' '}
                  <span className="font-bold text-slate-700">Color</span> &{' '}
                  <span className="font-bold text-slate-700">Size</span> columns to assign variant
                  descriptions.
                </li>
                <li>6. Drag the saved CSV into the selector and click confirm.</li>
              </ul>
            </div>
          </div>

          {/* Parsed Products Table Grid Preview (Right 2/3) */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm space-y-4">
              <div className="border-b border-slate-100 pb-3 flex justify-between items-center">
                <div>
                  <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Grid size={14} className="text-emerald-600" />
                    <span>Spreadsheet Row Preview & Validations</span>
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Review spreadsheet rows before committing them to the store catalog database.
                  </p>
                </div>
                {parsedProducts.length > 0 && (
                  <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold border border-emerald-100 px-2 py-0.5 rounded-full shrink-0">
                    {parsedProducts.length} rows loaded
                  </span>
                )}
              </div>

              {bulkIngestError && (
                <div className="p-3.5 bg-rose-50 border border-rose-150 text-xs text-rose-700 font-bold rounded-xl flex items-start gap-2 whitespace-pre-line">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  <span>{bulkIngestError}</span>
                </div>
              )}
              {bulkIngestSuccess && (
                <div className="p-3.5 bg-emerald-50 border border-emerald-150 text-xs text-emerald-750 font-bold rounded-xl flex items-center gap-2">
                  <CheckCircle2 size={15} />
                  <span>{bulkIngestSuccess}</span>
                </div>
              )}

              {parsedProducts.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                  <FileSpreadsheet
                    className="mx-auto mb-3 opacity-30 text-emerald-600 animate-pulse"
                    size={48}
                  />
                  <p className="text-xs font-bold">No spreadsheet rows parsed yet.</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Please select your filled CSV template in the left panel to populate preview.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="overflow-x-auto border border-slate-100 rounded-lg max-h-[350px] overflow-y-auto">
                    <table className="w-full text-left text-[11px] border-collapse">
                      <thead>
                        <tr className="border-b border-emerald-250 bg-emerald-50 text-emerald-800 font-bold uppercase tracking-wider font-sans text-[10px] sticky top-0 z-10">
                          <th className="py-2.5 px-3 text-center">
                            S.No.
                          </th>
                          <th className="py-2.5 px-3">
                            SKU Code
                          </th>
                          <th className="py-2.5 px-2">
                            Product Label *
                          </th>
                          <th className="py-2.5 px-2">
                            Category *
                          </th>
                          {isGstEnabled && <th className="py-2.5 px-2">HSN Code</th>}
                          <th className="py-2.5 px-2 text-right">
                            Cost (₹) *
                          </th>
                          <th className="py-2.5 px-2 text-right">
                            MRP (₹) *
                          </th>
                          <th className="py-2.5 px-2 text-right">
                            Discount (%)
                          </th>
                          <th className="py-2.5 px-2 text-right">
                            Selling (₹)
                          </th>
                          <th className="py-2.5 px-2 text-center">Stock</th>
                          <th className="py-2.5 px-2 text-center">Color</th>
                          <th className="py-2.5 px-2 text-center">Size</th>
                          <th className="py-2.5 px-3 text-center">Status / Alert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedProducts.map((p, idx) => {
                          const isSkuEmpty = !p.sku.trim();
                          const isNameEmpty = !p.name.trim();
                          const isNegativeMargin = p.costPrice > p.sellingPrice;
                          const hasIssue = isSkuEmpty || isNameEmpty || isNegativeMargin;

                          return (
                            <tr
                              key={idx}
                              className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${hasIssue ? 'bg-rose-50/20' : ''}`}
                            >
                              <td className="py-3 px-3 text-center font-bold text-slate-500 bg-emerald-50/10">{idx + 1}</td>
                              <td
                                className={`py-3 px-3 font-mono font-bold bg-emerald-50/10 ${isSkuEmpty ? 'text-rose-600' : 'text-slate-700'}`}
                              >
                                {isSkuEmpty ? '[SKU Required]' : p.sku}
                              </td>
                              <td
                                className={`py-3 px-2 font-bold bg-emerald-50/10 ${isNameEmpty ? 'text-rose-600' : 'text-slate-800'}`}
                              >
                                {isNameEmpty ? '[Name Required]' : p.name}
                              </td>
                              <td className="py-3 px-2 text-slate-500 font-semibold bg-emerald-50/10">
                                {p.categoryName}
                              </td>
                              {isGstEnabled && (
                                <td className="py-3 px-2 text-slate-500 font-medium">
                                  {p.hsnCode || 'N/A'}
                                </td>
                              )}
                              <td className="py-3 px-2 text-right font-mono text-slate-650 bg-emerald-50/10">
                                ₹{p.costPrice.toFixed(2)}
                              </td>
                              <td className="py-3 px-2 text-right font-mono text-slate-650 bg-emerald-50/10">
                                ₹{p.mrp.toFixed(2)}
                              </td>
                              <td className="py-3 px-2 text-right font-mono text-slate-600 bg-emerald-50/10">
                                {p.discount_percentage}%
                              </td>
                              <td
                                className={`py-3 px-2 text-right font-mono font-semibold bg-emerald-50/10 ${isNegativeMargin ? 'text-rose-600' : 'text-slate-800'}`}
                              >
                                ₹{p.sellingPrice.toFixed(2)}
                              </td>
                              <td className="py-3 px-2 text-center font-bold font-mono text-slate-850">
                                {p.currentStock}
                              </td>
                              <td className="py-3 px-2 text-center text-slate-600 font-semibold">
                                {p.color || 'N/A'}
                              </td>
                              <td className="py-3 px-2 text-center text-slate-600 font-semibold">
                                {p.size || 'N/A'}
                              </td>
                              <td className="py-3 px-3 text-center">
                                {hasIssue ? (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100 text-[9px] font-bold">
                                    <AlertTriangle size={10} />
                                    <span>
                                      {isNegativeMargin ? 'Negative Margin' : 'Missing Info'}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 text-[9px] font-bold">
                                    <CheckCircle2 size={10} />
                                    <span>Valid Row</span>
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Submission triggers */}
                  <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      onClick={() => setParsedProducts([])}
                      className="px-4 py-2 bg-white border border-slate-200 text-xs font-semibold text-slate-650 rounded hover:bg-slate-100 active:scale-95 cursor-pointer shadow-sm"
                      disabled={isBulkSaving}
                    >
                      Clear Rows
                    </button>
                    <button
                      type="button"
                      disabled={
                        isBulkSaving ||
                        parsedProducts.some(
                          (p) => !p.sku.trim() || !p.name.trim() || p.costPrice > p.sellingPrice,
                        )
                      }
                      onClick={async () => {
                        setIsBulkSaving(true);
                        setBulkIngestError('');
                        setBulkIngestSuccess('');
                        try {
                          const headers = { 'x-tenant-id': user?.tenantId || 'tenant-1' };
                          await api.post(
                            '/inventory/products/bulk',
                            { products: parsedProducts },
                            { headers },
                          );
                          setBulkIngestSuccess(
                            `Successfully ingested ${parsedProducts.length} products into the catalog!`,
                          );
                          setParsedProducts([]);
                          fetchProducts();
                          setTimeout(() => setBulkIngestSuccess(''), 4000);
                        } catch (err: any) {
                          setBulkIngestError(
                            err.response?.data?.message || 'Failed to save spreadsheet products.',
                          );
                        } finally {
                          setIsBulkSaving(false);
                        }
                      }}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded hover:shadow active:scale-95 cursor-pointer flex items-center gap-1.5 border border-emerald-500"
                    >
                      {isBulkSaving ? (
                        <span>Saving...</span>
                      ) : (
                        <>
                          <CheckCircle2 size={13} />
                          <span>Commit Spreadsheet Catalog</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ─── LABEL PRINTER & BARCODE GENERATOR VIEW ─────────────── */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Label settings form (Left 1/3) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Bluetooth connection sub-card */}
            {printConfig?.connection_mode !== 'USB' && (
              <div className="card bg-white border border-slate-200 p-5 rounded-xl shadow-sm space-y-4">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Bluetooth
                      size={16}
                      className={isPrinterConnected ? 'text-emerald-600' : 'text-slate-400'}
                    />
                    <span>Wireless Printer Connection</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Natively pair a 58mm Bluetooth thermal sticker printer.
                  </p>
                </div>

                <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-xs">
                  <div className="truncate pr-2">
                    <span className="font-semibold block text-slate-700 truncate">
                      {isPrinterConnected ? `Connected: ${pairedDevice}` : 'Printer Disconnected'}
                    </span>
                    {printerError && (
                      <span className="text-[10px] text-red-500 font-bold block mt-0.5 whitespace-normal">
                        {printerError}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={isPrinterConnected ? disconnectPrinter : connectPrinter}
                    className={`px-3 py-1.5 text-[10px] font-bold rounded cursor-pointer transition-all shrink-0 ${isPrinterConnected
                      ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                      }`}
                  >
                    {isPrinterConnected ? 'Disconnect' : 'Connect'}
                  </button>
                </div>
              </div>
            )}

            <div className="card bg-white border border-slate-200 p-5 rounded-xl shadow-sm space-y-4">
              <div>
                <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Package size={16} className="text-emerald-600" />
                  <span>Label Setup Parameters</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Define sticker properties, barcode formats, and layout specifications.
                </p>
              </div>

              {/* Product selector */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">
                  Select Target Product *
                </label>
                <select
                  value={selectedSkuForLabel}
                  onChange={(e) => setSelectedSkuForLabel(e.target.value)}
                  className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 bg-white text-slate-800"
                >
                  <option value="">-- Choose Catalog Product --</option>
                  {products.map((p) => (
                    <option key={p.sku} value={p.sku}>
                      {p.name} ({p.sku}) - ₹{p.sellingPrice}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format selection */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">
                  Label Format Option
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setLabelFormat('BARCODE')}
                    className={`py-1.5 text-xs font-semibold rounded border cursor-pointer text-center transition-all ${labelFormat === 'BARCODE'
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm font-bold'
                      : 'bg-white border-slate-200 text-slate-650 hover:bg-slate-50'
                      }`}
                  >
                    Standard Barcode
                  </button>
                  <button
                    type="button"
                    onClick={() => setLabelFormat('QR_CODE')}
                    className={`py-1.5 text-xs font-semibold rounded border cursor-pointer text-center transition-all ${labelFormat === 'QR_CODE'
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm font-bold'
                      : 'bg-white border-slate-200 text-slate-655 hover:bg-slate-50'
                      }`}
                  >
                    Dynamic QR Code
                  </button>
                </div>
              </div>

              {/* Sticker size selector */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">
                  Sticker Sticker Size
                </label>
                <select
                  value={labelSize}
                  onChange={(e) => setLabelSize(e.target.value as any)}
                  className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 bg-white text-slate-800"
                >
                  <option value="2x1">2.0" x 1.0" (51mm x 25mm) - Standard</option>
                  <option value="3x2">3.0" x 2.0" (76mm x 51mm) - Large</option>
                  <option value="4x3">4.0" x 3.0" (102mm x 76mm) - Shipping Box</option>
                </select>
              </div>

              {/* Copies to print */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase">
                  Print Quantities (Copies)
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={labelCopies}
                  onChange={(e) =>
                    setLabelCopies(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))
                  }
                  className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-mono text-slate-800"
                />
              </div>

              {/* Generate Trigger */}
              <div className="flex flex-col gap-2 pt-2">
                {printConfig?.connection_mode === 'USB' ? (
                  <button
                    type="button"
                    disabled={!selectedSkuForLabel}
                    onClick={() => {
                      const prod = products.find((p) => p.sku === selectedSkuForLabel);
                      if (!prod) return;

                      // Create dynamic styling for printing matching the chosen label dimensions
                      const dimensions =
                        labelSize === '2x1'
                          ? { width: '51mm', height: '25mm' }
                          : labelSize === '3x2'
                            ? { width: '76mm', height: '51mm' }
                            : { width: '102mm', height: '76mm' };

                      // Generate dynamic label elements in a printable hidden frame or tab
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) return;

                      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(prod.sku)}`;

                      let labelContent = '';
                      for (let i = 0; i < labelCopies; i++) {
                        labelContent += `
                          <div class="label-page">
                            <div class="label-container" style="box-sizing: border-box; width: ${dimensions.width}; height: ${dimensions.height}; padding: 2mm 3mm; font-family: monospace; display: flex; flex-direction: column; justify-content: space-between; align-items: center; overflow: hidden; page-break-after: always; position: relative; border: 1px solid #000; text-align: center; background-color: #ffffff;">
                              <!-- Top of Barcode: Shop Name, Product Name, SKU, Price -->
                              <div style="width: 100%; display: flex; flex-direction: column; align-items: center; gap: 0.5px;">
                                <div style="font-size: 10px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; width: 100%; padding-bottom: 1px; margin-bottom: 1px;">
                                  ${user?.tenantName || 'TEXTILE STORE'}
                                </div>
                                <div style="font-size: 8px; font-weight: bold; color: #000; max-width: 95%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-top: 1px;">
                                  ${prod.name}
                                </div>
                                <div style="font-size: 7.5px; color: #000; font-weight: bold; font-family: monospace; margin-top: 0.5px;">
                                  SKU: ${prod.sku}
                                </div>
                                <div style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 5px; font-size: 7.5px; font-weight: bold; margin-top: 1px;">
                                  ${prod.mrp && prod.mrp > prod.sellingPrice ? `
                                    <span style="color: #000; line-through;">MRP: ₹${prod.mrp.toFixed(2)}</span>
                                  ` : ''}
                                  <span style="font-weight: 900; font-size: 9px; color: #000; background-color: #f1f5f9; padding: 0.5px 3px; border-radius: 2px;">SALE: ₹${prod.sellingPrice.toFixed(2)}</span>
                                </div>
                              </div>
                              
                              <!-- Center/Bottom: Barcode (Full size, big) -->
                              <div style="width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; margin: 1mm 0 0 0;">
                                ${labelFormat === 'QR_CODE'
                            ? `<div style="width: 32px; height: 32px;"><img src="${qrSrc}" style="width: 100%; height: 100%; object-fit: contain;" /></div>`
                            : `<svg class="barcode-svg" data-value="${prod.sku}" style="max-height: 28px; max-width: 88%; display: block; margin: 0 auto;"></svg>`
                          }
                                <div style="font-size: 6.5px; letter-spacing: 1.5px; margin-top: 1px; color: #000; font-weight: bold; text-align: center;">
                                  *${prod.sku}*
                                </div>
                              </div>
                            </div>
                          </div>
                        `;
                      }

                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Print Labels - ${prod.sku}</title>
                            <style>
                              body { margin: 0; padding: 0; }
                              @page { size: ${dimensions.width} ${dimensions.height}; margin: 0; }
                              @media print {
                                .label-page { page-break-after: always; }
                                .label-container { border: none !important; }
                              }
                            </style>
                            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
                          </head>
                          <body>
                            ${labelContent}
                            <script>
                              window.onload = function() {
                                try {
                                  const elements = document.querySelectorAll('.barcode-svg');
                                  elements.forEach(el => {
                                    const val = el.getAttribute('data-value');
                                    if (val) {
                                      JsBarcode(el, val, {
                                        format: "CODE128",
                                        width: 1.25,
                                        height: 22,
                                        displayValue: false,
                                        margin: 2
                                      });
                                    }
                                  });
                                } catch (e) {
                                  console.error("Barcode rendering failed:", e);
                                }
                                setTimeout(() => {
                                  window.print();
                                  window.close();
                                }, 300);
                              };
                            </script>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                    }}
                    className="w-full py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-emerald-500"
                  >
                    <span>Print Labels (USB)</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!selectedSkuForLabel || isPrinterPrinting}
                    onClick={async () => {
                      const prod = products.find((p) => p.sku === selectedSkuForLabel);
                      if (!prod) return;

                      if (!isPrinterConnected) {
                        const ok = await connectPrinter();
                        if (!ok) return;
                      }

                      const payload = {
                        sku: prod.sku,
                        name: prod.name,
                        categoryName: prod.category || 'Textiles',
                        price: prod.sellingPrice,
                        mrp: prod.mrp,
                        discount_percentage: prod.discount_percentage,
                        retail_price: prod.sellingPrice,
                        brandName: user?.tenantName || 'Katirtek'
                      };

                      const labelsList = Array(labelCopies).fill(payload);
                      const byteData = compileWirelessLabelsBulk(labelsList);
                      await sendPrintJob(byteData);
                    }}
                    className="w-full py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-emerald-500"
                  >
                    <Bluetooth size={13} />
                    <span>
                      {isPrinterPrinting ? 'Printing Labels...' : 'Print over BLE (Wireless 58mm)'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Real-time preview card (Right 2/3) */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card bg-slate-50 border border-slate-200 p-6 rounded-xl space-y-4">
              <div>
                <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">
                  Sticker Label Preview
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Real-time preview of the printable label layout matching chosen specifications.
                </p>
              </div>

              {selectedSkuForLabel ? (
                (() => {
                  const prod = products.find((p) => p.sku === selectedSkuForLabel)!;
                  const sizeStyles =
                    labelSize === '2x1'
                      ? { width: '380px', height: '190px' }
                      : labelSize === '3x2'
                        ? { width: '480px', height: '320px' }
                        : { width: '560px', height: '420px' };

                  return (
                    <div className="flex items-center justify-center bg-white border border-dashed border-slate-300 rounded-xl p-8 shadow-inner min-h-[300px]">
                      <div
                        style={sizeStyles}
                        className="border border-slate-900 bg-white p-5 rounded font-mono text-slate-950 flex flex-col justify-between shadow-md transition-all duration-200 relative"
                      >
                        <div className="text-center font-bold text-[10px] tracking-wider border-b border-dashed border-slate-300 pb-1.5 uppercase">
                          {user?.tenantName || 'YOUR TEXTILE STORE'}
                        </div>

                        <div className="flex items-center justify-between gap-4 flex-1 my-4">
                          <div className="space-y-1.5 flex-1">
                            <p className="font-extrabold text-xs text-slate-900 line-clamp-2">
                              {prod.name}
                            </p>
                            <div className="text-[10px] space-y-0.5 text-slate-655">
                              <p>
                                SKU: <span className="font-bold text-slate-800">{prod.sku}</span>
                              </p>
                              {prod.mrp && prod.mrp > prod.sellingPrice ? (
                                <>
                                  <p>
                                    MRP:{' '}
                                    <span className="font-bold text-slate-500 line-through text-xs font-mono">
                                      ₹{prod.mrp.toFixed(2)}
                                    </span>
                                  </p>
                                  <p>
                                    SALE:{' '}
                                    <span className="font-extrabold text-emerald-800 text-xs font-mono">
                                      ₹{prod.sellingPrice.toFixed(2)}
                                    </span>
                                  </p>
                                </>
                              ) : (
                                <p>
                                  PRICE:{' '}
                                  <span className="font-bold text-emerald-800 text-xs font-mono">
                                    ₹{prod.sellingPrice.toFixed(2)}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="w-20 h-20 bg-slate-50 border border-slate-200 rounded flex items-center justify-center p-1 shrink-0 select-none">
                            {labelFormat === 'QR_CODE' ? (
                              <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(prod.sku)}`}
                                alt="QR Code"
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center w-full">
                                <div className="flex items-end h-10 w-full justify-center px-1 overflow-hidden">
                                  {[...Array(24)].map((_, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        width: idx % 3 === 0 ? '3px' : '1px',
                                        marginRight: '2px',
                                      }}
                                      className="bg-black h-8 shrink-0"
                                    />
                                  ))}
                                </div>
                                <span className="text-[8px] tracking-widest mt-1 block font-mono text-slate-500">
                                  {prod.sku}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-center text-[9px] text-slate-450 font-bold border-t border-dashed border-slate-300 pt-1.5 tracking-wider">
                          * CUSTOMS SPECIFIED COMPLIANCE LABEL *
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex items-center justify-center bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center text-slate-450 min-h-[300px]">
                  <div>
                    <Package size={36} className="mx-auto mb-2 opacity-35 text-emerald-600" />
                    <p className="text-xs font-bold text-slate-655">No product selected</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      Select a catalog product on the left side configuration to load label
                      dimensions and barcodes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden border border-slate-100 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
              <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                Edit Catalog Item: {editingItem.sku}
              </h3>
              <button
                onClick={() => setEditingItem(null)}
                className="text-slate-400 hover:text-slate-650 cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              {updateError && (
                <div className="text-[11px] text-red-550 font-bold bg-red-50 border border-red-100 p-2 rounded">
                  {updateError}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[9px] font-extrabold text-slate-500 uppercase">Item Name</label>
                <input
                  type="text"
                  disabled
                  value={editingItem.name}
                  className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded bg-slate-50 text-slate-500 font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-extrabold text-slate-500 uppercase">Cost Price (₹)</label>
                  <input
                    type="number"
                    required
                    value={editCostPrice}
                    onChange={(e) => handleEditFieldChange('COST', parseFloat(e.target.value) || 0)}
                    className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-mono font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-extrabold text-slate-500 uppercase">Stock Count (Remaining)</label>
                  <input
                    type="number"
                    required
                    value={editStock}
                    onChange={(e) => handleEditFieldChange('STOCK', parseFloat(e.target.value) || 0)}
                    className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-mono font-medium"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 bg-amber-50/20 p-3 rounded-lg border border-amber-100/50">
                <div className="space-y-1">
                  <label className="text-[8px] font-extrabold text-amber-800 uppercase">MRP (₹)</label>
                  <input
                    type="number"
                    required
                    value={editMrp}
                    onChange={(e) => handleEditFieldChange('MRP', parseFloat(e.target.value) || 0)}
                    className="block w-full px-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:border-emerald-600 font-mono font-bold text-amber-900 bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-extrabold text-amber-800 uppercase">Disc %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editDiscountPercentage}
                    onChange={(e) => handleEditFieldChange('DISCOUNT', parseFloat(e.target.value) || 0)}
                    className="block w-full px-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:border-emerald-600 font-mono text-amber-950 bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-extrabold text-emerald-850 uppercase">Selling (₹)</label>
                  <input
                    type="number"
                    required
                    value={editSellingPrice}
                    onChange={(e) => handleEditFieldChange('SELL', parseFloat(e.target.value) || 0)}
                    className="block w-full px-2 py-1 text-xs border border-emerald-200 rounded focus:outline-none focus:border-emerald-600 font-mono font-bold text-emerald-900 bg-white"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 mt-2">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="px-3 py-1.5 bg-white border border-slate-200 text-xs font-semibold text-slate-650 rounded hover:bg-slate-100 cursor-pointer shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded cursor-pointer border border-emerald-500 shadow-sm"
                >
                  {isUpdating ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
